// security/permissions.ts
import type { TeamConnectionRoleSchemaModel, TeamRole, TeamRoleSchemaModel } from './type';

export const roleRank: Record<TeamRole, number> = {
  Administrator: 3,
  Contributor: 2,
  Reader: 1,
};

export type PermissionMap = Record<string, TeamRole>;
export type ConnectorPermissionMap = { [key: string]: Record<string, TeamRole> };

/**
 * Build base path -> role map (no inheritance yet).
 * If multiple entries for same path exist, highest privilege wins.
 */
export function buildPermissionMap(schema: TeamRoleSchemaModel[] | null): PermissionMap {
  const map: PermissionMap = {};
  if (schema != null) {
    for (const item of schema) {
      if (!item.role) continue;
      const current = map[item.path];
      if (!current || roleRank[item.role] > roleRank[current]) {
        map[item.path] = item.role;
      }
    }
  }
  return map;
}

export function buildConnectorPermissionMap(
  schema: TeamConnectionRoleSchemaModel | null,
): ConnectorPermissionMap {
  const map: ConnectorPermissionMap = {};
  if (schema == null) return {};

  const schemaKeys: string[] = Object.keys(schema);
  if (schema != null && schemaKeys.length > 0) {
    for (const schemaKey in schemaKeys) {
      const connectorId = schemaKeys[schemaKey];

      const connectorItems = schema[connectorId];
      if (!Array.isArray(connectorItems)) continue;
      for (const item of connectorItems) {
        if (!item.role) continue;
        if (!map[connectorId]) map[connectorId] = {};

        const current = map[connectorId][item.path];
        if (!current || roleRank[item.role] > roleRank[current])
          map[connectorId][item.path] = item.role;
      }
    }
  }
  return map;
}

/**
 * Resolve effective role for a path, with inheritance:
 * e.g. "Data Sources/File":
 *   check "Data Sources" then "Data Sources/File"
 *   and return highest role found along the way.
 */
export function getEffectiveRole(map: PermissionMap, path: string): TeamRole | null {
  if (!path) return null;
  if (map[path] != null) return map[path]; // NOTE - child overriding the parent if it is defined
  //console.log('[getEffectiveRole] map =', map);

  const segments = path.split('/').filter(Boolean);
  let current = '';
  let best: TeamRole | null = null;

  for (const seg of segments) {
    current = current ? `${current}/${seg.trim()}` : seg.trim(); // "Data Sources", then "Data Sources/File"
    //console.log('[getEffectiveRole] current =', current);
    const role = map[current];
    //console.log('[getEffectiveRole] role =', role);

    if (role && (!best || roleRank[role] > roleRank[best])) {
      best = role;
      //console.log('[getEffectiveRole] best =', best);
    }
  }
  return best;
}

export function getConnectorEffectiveRole(
  connectorMap: ConnectorPermissionMap,
  map: PermissionMap,
  connectorId: string,
  path: string,
): TeamRole | null {
  if (!path) return null;
  if (connectorId == null) return getEffectiveRole(map, path);
  if (!connectorMap[connectorId]) return getEffectiveRole(map, path);

  // Walk path segments within the connector's override map (with inheritance)
  const segments = path.split('/').filter(Boolean);
  let current = '';
  let best: TeamRole | null = null;

  for (const seg of segments) {
    current = current ? `${current}/${seg.trim()}` : seg.trim();
    const role = connectorMap[connectorId][current];
    if (role && (!best || roleRank[role] > roleRank[best])) {
      best = role;
    }
  }

  // The top-level "Connectors" role cascades to all paths within this connector's scope
  // (e.g. Connectors=Administrator flows down to Data Sources/Query when no explicit override is set)
  const connectorTopRole = connectorMap[connectorId]['Connectors'];
  if (connectorTopRole && (!best || roleRank[connectorTopRole] > roleRank[best])) {
    best = connectorTopRole;
  }

  return best ?? getEffectiveRole(map, path);
}
